import React, { useState, useRef, useEffect, KeyboardEvent } from "react";
import { createPortal } from "react-dom";

export interface AutoCompleteOption {
  value: string;
  label: string;
}

interface AutoCompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (option: AutoCompleteOption | string) => void;
  options: Array<AutoCompleteOption | string>;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  dropdownClassName?: string;
  optionClassName?: string;
  disabled?: boolean;
  loading?: boolean;
  noOptionsMessage?: string;
  filterOption?: (option: AutoCompleteOption | string, inputValue: string) => boolean;
  renderOption?: (option: AutoCompleteOption | string, isHighlighted: boolean) => React.ReactNode;
  autoFocus?: boolean;
  clearOnSelect?: boolean;
  showDropdownOnFocus?: boolean;
  portalContainer?: HTMLElement | null;
}

export default function AutoComplete({
  value,
  onChange,
  onSelect,
  options,
  placeholder = "",
  className = "",
  inputClassName = "",
  dropdownClassName = "",
  optionClassName = "",
  disabled = false,
  loading = false,
  filterOption,
  renderOption,
  autoFocus = false,
  clearOnSelect = false,
  showDropdownOnFocus = true,
  portalContainer = null,
}: AutoCompleteProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [filteredOptions, setFilteredOptions] = useState<Array<AutoCompleteOption | string>>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // Ensure value is a string before calling trim()
  const stringValue = typeof value === 'string' ? value : '';

  // Update dropdown position when showing
  useEffect(() => {
    if (showDropdown && portalContainer && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const inputRect = inputRef.current?.getBoundingClientRect() || rect;
      
      setDropdownPosition({
        top: inputRect.bottom + window.scrollY,
        left: inputRect.left + window.scrollX,
        width: inputRect.width
      });
    }
  }, [showDropdown, portalContainer]);

  // Filter options based on input value
  useEffect(() => {
    let filtered: Array<AutoCompleteOption | string> = [];
    
    if (stringValue.trim() === "" && showDropdownOnFocus) {
      filtered = options;
    } else if (stringValue.trim() !== "") {
      // Check if there's an exact match
      const hasExactMatch = options.some(option => {
        const optionLabel = typeof option === "string" ? option : option.label;
        const optionValue = typeof option === "string" ? option : option.value;
        
        return (
          optionLabel.toLowerCase() === stringValue.toLowerCase() ||
          optionValue.toLowerCase() === stringValue.toLowerCase()
        );
      });
      
      // If there's an exact match, don't show any suggestions
      if (hasExactMatch) {
        filtered = [];
      } else if (filterOption) {
        filtered = options.filter(option => filterOption(option, stringValue));
      } else {
        filtered = options.filter(option => {
          const optionLabel = typeof option === "string" ? option : option.label;
          const optionValue = typeof option === "string" ? option : option.value;
          
          return (
            optionLabel.toLowerCase().includes(stringValue.toLowerCase()) ||
            optionValue.toLowerCase().includes(stringValue.toLowerCase())
          );
        });
      }
    }
    
    setFilteredOptions(filtered);
    
    // Reset highlighted index when filtered options change
    setHighlightedIndex(filtered.length > 0 ? 0 : -1);
  }, [value, options, filterOption, showDropdownOnFocus]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target as Node) &&
        ((dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) ||
         (!dropdownRef.current))
      ) {
        setShowDropdown(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (showDropdown && highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        const containerRect = dropdownRef.current.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();
        
        if (elementRect.bottom > containerRect.bottom) {
          dropdownRef.current.scrollTop += elementRect.bottom - containerRect.bottom;
        } else if (elementRect.top < containerRect.top) {
          dropdownRef.current.scrollTop -= containerRect.top - elementRect.top;
        }
      }
    }
  }, [highlightedIndex, showDropdown]);

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        setShowDropdown(true);
        e.preventDefault();
      }
      return;
    }
    
    switch (e.key) {
      case "ArrowDown":
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        e.preventDefault();
        break;
      case "ArrowUp":
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
        e.preventDefault();
        break;
      case "Enter":
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        e.preventDefault();
        break;
      case "Escape":
        setShowDropdown(false);
        e.preventDefault();
        break;
      case "Tab":
        setShowDropdown(false);
        break;
      default:
        break;
    }
  };

  // Handle option selection
  const handleSelect = (option: AutoCompleteOption | string) => {
    if (onSelect) {
      onSelect(option);
    } else {
      const newValue = typeof option === "string" ? option : option.value;
      onChange(newValue);
    }
    
    if (clearOnSelect) {
      onChange("");
    }
    
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  // Render option
  const renderOptionItem = (option: AutoCompleteOption | string, index: number) => {
    if (renderOption) {
      return renderOption(option, index === highlightedIndex);
    }
    
    const optionLabel = typeof option === "string" ? option : option.label;
    
    return (
      <div
        key={typeof option === "string" ? option : option.value}
        className={`px-3 py-2 cursor-pointer text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 ${
          index === highlightedIndex ? "bg-gray-100 dark:bg-gray-600" : ""
        } ${optionClassName}`}
        onClick={() => handleSelect(option)}
        onMouseEnter={() => setHighlightedIndex(index)}
      >
        {optionLabel}
      </div>
    );
  };

  // Render dropdown either normally or via portal
  const renderDropdown = () => {
    if (!showDropdown || filteredOptions.length === 0) return null;

    const dropdownContent = (
      <div 
        ref={dropdownRef}
        className={`bg-white dark:bg-gray-700 shadow-lg rounded-md border border-gray-200 dark:border-gray-600 max-h-60 overflow-auto pointer-events-auto ${dropdownClassName}`}
        style={{ 
          maxHeight: "240px",
          ...(portalContainer ? {
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999
          } : {})
        }}
      >
        {loading ? (
          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
            Loading...
          </div>
        ) : (
          filteredOptions.map((option, index) => renderOptionItem(option, index))
        )}
      </div>
    );

    if (portalContainer) {
      return createPortal(dropdownContent, portalContainer);
    }

    // Regular non-portal rendering
    return (
      <div className="absolute z-50 mt-1 w-full">
        {dropdownContent}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => {
            if (showDropdownOnFocus) {
              setShowDropdown(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={`w-full pl-2 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring rounded focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out ${inputClassName}`}
        />
        {/* Use stringValue for consistency with the rest of the component */}
        {stringValue && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none"
            aria-label="Clear input"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      {renderDropdown()}
    </div>
  );
}
